'use client'

import { useState, useEffect, useRef } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { createClient } from '@/lib/supabase/client-component'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Bot } from 'lucide-react'
import { useLocale } from '@/lib/i18n'
import { setUserContext } from '@/lib/sentry/capture'
import { GoogleOAuthButton } from '@/components/auth/google-oauth-button'
import { takeConsentReturn } from '@/lib/auth/oauth-consent-return'
import { PasswordInput } from '@/components/auth/password-input'
import { Captcha, getCaptchaToken, resetCaptcha, isCaptchaError } from '@/components/auth/captcha'
import type { TurnstileInstance } from '@marsidev/react-turnstile'

interface AuthModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultTab?: 'login' | 'register'
}

export function AuthModal({ open, onOpenChange, defaultTab = 'login' }: AuthModalProps) {
  const { t } = useLocale()
  const router = useRouter()
  const searchParams = useSearchParams()
  const [activeTab, setActiveTab] = useState<'login' | 'register'>(defaultTab)
  const [view, setView] = useState<'auth' | 'forgot_password'>('auth')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  // MFA state
  const [mfaStep, setMfaStep] = useState(false)
  const [mfaCode, setMfaCode] = useState('')
  const [mfaFactorId, setMfaFactorId] = useState('')
  const [mfaChallengeId, setMfaChallengeId] = useState('')
  const [pendingSession, setPendingSession] = useState<{ access_token: string; user_id: string; user_email: string | undefined } | null>(null)
  const captchaRef = useRef<TurnstileInstance | null>(null)
  const supabase = createClient()

  // Remove ?auth=open from URL and clear any stale localStorage when modal closes
  useEffect(() => {
    if (!open && searchParams.get('auth') === 'open') {
      // Remove the auth parameter from URL
      const newUrl = window.location.pathname
      router.replace(newUrl)
    }
    // Clear auth state when modal closes (especially for failed login attempts)
    if (!open) {
      // Clear error and message states
      setError('')
      setMessage('')
      // Clear form fields
      setPassword('')
      setConfirmPassword('')
      setShowPassword(false)
      setShowConfirmPassword(false)
      // Clear MFA state
      setMfaStep(false)
      setMfaCode('')
      setMfaFactorId('')
      setMfaChallengeId('')
      setPendingSession(null)
      // Reset view
      setView('auth')
    }
  }, [open, searchParams, router])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    try {
      const captchaToken = await getCaptchaToken(captchaRef)
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken },
      })

      if (error) {
        setError(isCaptchaError(error) ? t('auth.captcha_failed') : error.message)
        return
      }

      // Session is persisted into the @supabase/ssr cookie by signInWithPassword.
      // Check if user has MFA enabled.
      if (data.session && data.user) {
        const { data: factors } = await (supabase as any).auth.mfa.listFactors()
        const totpFactors = factors?.totp ?? []

        if (totpFactors.length > 0) {
          // User has 2FA — challenge first factor
          const factorId = totpFactors[0].id
          const { data: challengeData, error: challengeError } = await (supabase as any).auth.mfa.challenge({ factorId })
          if (challengeError) {
            setError(challengeError.message)
            return
          }
          // Store pending session info for after MFA verify
          setPendingSession({
            access_token: data.session.access_token,
            user_id: data.user.id,
            user_email: data.user.email ?? undefined,
          })
          setMfaFactorId(factorId)
          setMfaChallengeId(challengeData.id)
          setMfaStep(true)
          return
        }

        // No MFA — set Sentry user context for error tracking
        setUserContext({
          id: data.user.id,
          email: data.user.email || undefined,
        })
      }

      setMessage(t('auth.login_successful'))
      // Close modal and redirect
      setTimeout(() => {
        onOpenChange(false)
        window.location.href = takeConsentReturn({ onlyOnConsentPage: true }) ?? '/dashboard'
      }, 500)

    } catch (err) {
      setError(isCaptchaError(err) ? t('auth.captcha_failed') : t('auth.connection_error'))
    } finally {
      resetCaptcha(captchaRef)
      setLoading(false)
    }
  }

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const { error } = await (supabase as any).auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: mfaChallengeId,
        code: mfaCode,
      })

      if (error) {
        setError(t('auth.two_factor_invalid_code'))
        return
      }

      // MFA verified — the AAL2 session is now in the cookie. Just update Sentry context.
      if (pendingSession) {
        setUserContext({
          id: pendingSession.user_id,
          email: pendingSession.user_email,
        })
      }

      setMessage(t('auth.login_successful'))
      setTimeout(() => {
        onOpenChange(false)
        window.location.href = takeConsentReturn({ onlyOnConsentPage: true }) ?? '/dashboard'
      }, 500)
    } catch (err) {
      setError(t('auth.connection_error'))
    } finally {
      setLoading(false)
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    // Validate passwords match
    if (password !== confirmPassword) {
      setError(t('auth.passwords_do_not_match') || 'Passwords do not match')
      setLoading(false)
      return
    }

    try {
      const captchaToken = await getCaptchaToken(captchaRef)
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          captchaToken,
          data: {
            full_name: email.split('@')[0]
          }
        }
      })

      if (error) {
        setError(isCaptchaError(error) ? t('auth.captcha_failed') : error.message)
        return
      }

      // Check if email confirmation is required (session is null)
      if (!data.session) {
        // Email confirmation is required
        setMessage(t('auth.register_confirmation_sent'))
      } else {
        // User is automatically signed in (email confirmation disabled). The
        // session is already persisted in the @supabase/ssr cookie — just redirect.
        setMessage(t('auth.register_successful'))
        if (data.user) {
          setTimeout(() => {
            onOpenChange(false)
            window.location.href = takeConsentReturn({ onlyOnConsentPage: true }) ?? '/dashboard'
          }, 1500)
        }
      }

    } catch (err) {
      setError(isCaptchaError(err) ? t('auth.captcha_failed') : t('auth.connection_error'))
    } finally {
      resetCaptcha(captchaRef)
      setLoading(false)
    }
  }

  const handleTabChange = (tab: 'login' | 'register') => {
    setActiveTab(tab)
    setError('')
    setMessage('')
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    try {
      const captchaToken = await getCaptchaToken(captchaRef)
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/auth/reset-password',
        captchaToken,
      })

      if (error) {
        setError(isCaptchaError(error) ? t('auth.captcha_failed') : error.message)
        return
      }

      setMessage(t('auth.forgot_password_email_sent'))
    } catch (err) {
      setError(isCaptchaError(err) ? t('auth.captcha_failed') : t('auth.connection_error'))
    } finally {
      resetCaptcha(captchaRef)
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-background rounded-2xl shadow-xl border-0 p-0 overflow-hidden">
        {/* Header with gradient background */}
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 px-6 pt-6 pb-8">
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
              <Bot className="h-7 w-7 text-white" />
            </div>
            <DialogTitle className="text-2xl font-bold text-white mb-1">
              Chatbotty
            </DialogTitle>
            <DialogDescription className="text-white/90 text-sm">
              {view === 'forgot_password' ? t('auth.forgot_password_title') : (activeTab === 'login' ? t('auth.login_to_account') : t('auth.login'))}
            </DialogDescription>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className={`flex border-b border-gray-200 dark:border-gray-700 ${view === 'forgot_password' ? 'hidden' : ''}`}>
          <button
            onClick={() => handleTabChange('login')}
            className={`flex-1 py-3 text-sm font-medium transition-colors duration-200 ${
              activeTab === 'login'
                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {t('auth.signin')}
          </button>
          <button
            onClick={() => handleTabChange('register')}
            className={`flex-1 py-3 text-sm font-medium transition-colors duration-200 ${
              activeTab === 'register'
                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {t('auth.register')}
          </button>
        </div>

        {/* Form */}
        <div className="p-6">
          {view === 'forgot_password' ? (
            <form className="space-y-4" onSubmit={handleForgotPassword}>
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center">{t('auth.forgot_password_description')}</p>
              <Input
                type="email"
                placeholder={t('auth.email_placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
                className="h-11 px-4 rounded-xl border-gray-200 dark:border-gray-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-800 transition-all duration-200 dark:bg-gray-800 dark:text-gray-100"
              />
              <Button
                type="submit"
                disabled={loading || !email}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium py-3 rounded-xl transition-all duration-200"
              >
                {loading ? t('common.loading') : t('auth.forgot_password_title')}
              </Button>
              <button
                type="button"
                onClick={() => { setView('auth'); setError(''); setMessage('') }}
                className="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 py-1"
              >
                {t('auth.forgot_password_back')}
              </button>
            </form>
          ) : mfaStep ? (
            <form className="space-y-4" onSubmit={handleMfaVerify}>
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center">{t('auth.two_factor_info')}</p>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder={t('auth.two_factor_code_placeholder')}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                autoFocus
                className="h-11 px-4 rounded-xl border-gray-200 dark:border-gray-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-800 transition-all duration-200 tracking-widest text-center text-lg dark:bg-gray-800 dark:text-gray-100"
              />
              <Button
                type="submit"
                disabled={loading || mfaCode.length < 6}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium py-3 rounded-xl transition-all duration-200"
              >
                {loading ? t('common.loading') : t('auth.two_factor_verify')}
              </Button>
              <button
                type="button"
                onClick={() => { setMfaStep(false); setMfaCode(''); setError(''); setPendingSession(null) }}
                className="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 py-1"
              >
                {t('auth.two_factor_back')}
              </button>
            </form>
          ) : (
          <form
            id={activeTab === 'login' ? 'login-form' : 'register-form'}
            className="space-y-4"
            onSubmit={activeTab === 'login' ? handleLogin : handleRegister}
            autoComplete={activeTab === 'login' ? 'on' : 'off'}
          >
            <div>
              <Input
                type="email"
                name="email"
                id="email"
                placeholder={t('auth.email_placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="h-11 px-4 rounded-xl border-gray-200 dark:border-gray-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-800 transition-all duration-200 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
            <div>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                show={showPassword}
                onToggleShow={() => setShowPassword(!showPassword)}
                placeholder={t('auth.password_placeholder')}
                autoComplete={activeTab === 'login' ? 'current-password' : 'new-password'}
                id="password"
              />
            </div>

            {activeTab === 'register' && (
              <div>
                <PasswordInput
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  show={showConfirmPassword}
                  onToggleShow={() => setShowConfirmPassword(!showConfirmPassword)}
                  placeholder={t('auth.confirm_password_placeholder') || 'Confirm password'}
                  autoComplete="new-password"
                  id="confirm-password"
                />
              </div>
            )}

            {activeTab === 'login' && (
              <div className="text-right -mt-1">
                <button
                  type="button"
                  onClick={() => { setView('forgot_password'); setError(''); setMessage('') }}
                  className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-purple-600 dark:hover:text-purple-400"
                >
                  {t('auth.forgot_password')}
                </button>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !email || !password || (activeTab === 'register' && !confirmPassword)}
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium py-3 rounded-xl transition-all duration-200"
            >
              {loading
                ? (activeTab === 'login' ? t('auth.logging_in') : t('common.loading'))
                : (activeTab === 'login' ? t('auth.signin') : t('auth.register'))
              }
            </Button>

            {activeTab === 'login' && (
              <div className="text-center pt-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('auth.no_account')}</span>
                <button
                  type="button"
                  onClick={() => handleTabChange('register')}
                  disabled={loading}
                  className="ml-1 text-sm text-indigo-600 dark:text-indigo-400 hover:text-purple-600 dark:hover:text-purple-400 font-medium"
                >
                  {t('auth.register')}
                </button>
              </div>
            )}
          </form>
          )}

          {/* Turnstile stays invisible unless Cloudflare asks for a click */}
          {!mfaStep && <Captcha ref={captchaRef} />}

          {!mfaStep && view !== 'forgot_password' && (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-background text-gray-500 dark:text-gray-400">{t('auth.or_continue_with')}</span>
                </div>
              </div>
              <GoogleOAuthButton />
            </>
          )}

          {message && (
            <div className="mt-4 p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-xl text-green-800 dark:text-green-300 text-sm">
              {message}
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-xl text-red-800 dark:text-red-300 text-sm">
              {error}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
